import type { PetId } from '../core/state';
import { drawCat } from './sprites';
import { shade, shadow, type Ctx } from './paint';
import type { Pose } from './sprites';

/** Small companion pets, drawn with soft shapes to match the characters. */
export function drawPet(c: Ctx, kind: PetId, x: number, y: number, pose: Pose, hop: number, t: number): void {
  const d = pose.facing === 'left' ? -1 : 1;
  const yy = y - hop;
  const blob = (bx: number, by: number, rx: number, ry: number, fill: string, rot = 0) => {
    c.beginPath();
    c.ellipse(bx, by, rx, ry, rot, 0, Math.PI * 2);
    c.fillStyle = fill;
    c.fill();
    c.strokeStyle = shade(fill, -0.5);
    c.lineWidth = 0.5;
    c.stroke();
  };
  const eye = (ex: number, ey: number) => {
    c.fillStyle = '#231a24';
    c.beginPath();
    c.arc(ex, ey, pose.blink ? 0.3 : 0.6, 0, Math.PI * 2);
    c.fill();
    if (!pose.blink) {
      c.fillStyle = '#fff';
      c.fillRect(ex - 0.3, ey - 0.4, 0.35, 0.35);
    }
  };
  switch (kind) {
    case 'cat':
      return drawCat(c, x, yy, pose, t, true, '#9aa0ac');
    case 'rabbit': {
      shadow(c, x, y, 5, 1.8);
      const earWobble = Math.sin(t * 6) * (pose.moving ? 0.25 : 0.08);
      blob(x - d * 1, yy - 3.5, 4.2, 3.4, '#f7f4ef');
      blob(x + d * 3, yy - 6, 3, 2.8, '#f7f4ef');
      for (const s of [-0.6, 0.6]) {
        blob(x + d * 3 + s * 1.6, yy - 10, 0.9, 2.8, '#f7f4ef', s * 0.3 + earWobble);
        blob(x + d * 3 + s * 1.6, yy - 9.8, 0.4, 1.8, '#ffc2cf', s * 0.3 + earWobble);
      }
      blob(x - d * 4.6, yy - 4, 1.2, 1.2, '#ffffff');
      eye(x + d * 4, yy - 6.4);
      c.fillStyle = '#ff9fb0';
      c.fillRect(x + d * 5.6 - 0.3, yy - 5.6, 0.6, 0.5);
      return;
    }
    case 'dog': {
      shadow(c, x, y, 5.5, 1.8);
      const wag = Math.sin(t * (pose.moving ? 14 : 6)) * 0.4;
      blob(x - d * 4.5, yy - 6, 1.6, 1.2, '#e09a4a', wag);
      blob(x, yy - 3.5, 4.5, 3, '#e09a4a');
      blob(x, yy - 2.5, 2.6, 1.5, '#fbeedb');
      blob(x + d * 3.5, yy - 6.5, 3, 2.7, '#e09a4a');
      blob(x + d * 4.6, yy - 5.6, 1.6, 1.2, '#fbeedb');
      for (const s of [-1, 1]) {
        c.fillStyle = '#c4782e';
        c.beginPath();
        c.moveTo(x + d * 3.5 + s * 1.6, yy - 8.5);
        c.lineTo(x + d * 3.5 + s * 2.4, yy - 10.5);
        c.lineTo(x + d * 3.5 + s * 0.4, yy - 9);
        c.fill();
      }
      eye(x + d * 4, yy - 7);
      c.fillStyle = '#231a24';
      c.fillRect(x + d * 5.8 - 0.4, yy - 6, 0.8, 0.6);
      return;
    }
    case 'hedgehog': {
      shadow(c, x, y, 5, 1.6);
      c.fillStyle = '#6b4a32';
      c.beginPath();
      for (let i = 0; i <= 10; i++) {
        const a = Math.PI + (i / 10) * Math.PI;
        const r = i % 2 ? 5.2 : 4;
        c.lineTo(x - d * 0.5 + Math.cos(a) * r, yy - 1.5 + Math.sin(a) * r * 0.9);
      }
      c.fill();
      blob(x + d * 3.5, yy - 2.5, 2.2, 1.8, '#e8c9a0');
      eye(x + d * 4, yy - 3);
      c.fillStyle = '#231a24';
      c.fillRect(x + d * 5.6 - 0.3, yy - 2.6, 0.6, 0.6);
      return;
    }
    case 'hamster': {
      shadow(c, x, y, 4.5, 1.6);
      blob(x, yy - 3.5, 4.2, 3.6, '#f0b860');
      blob(x + d * 0.6, yy - 2.6, 2.6, 2.2, '#fff4e0');
      for (const s of [-1, 1]) blob(x + s * 2.4, yy - 6.8, 1.1, 1, '#f0b860');
      eye(x + d * 1.4 - 1.2, yy - 4.4);
      eye(x + d * 1.4 + 1.2, yy - 4.4);
      c.fillStyle = 'rgba(255,120,130,0.5)';
      c.fillRect(x + d * 1.4 - 2.6, yy - 3.4, 1, 0.6);
      return;
    }
    case 'croc': {
      shadow(c, x, y, 7, 1.6);
      const tail = Math.sin(t * 5) * 1;
      c.fillStyle = '#4f9a4a';
      c.beginPath();
      c.moveTo(x - d * 8, yy - 2 + tail);
      c.quadraticCurveTo(x - d * 3, yy - 5, x + d * 3, yy - 4);
      c.lineTo(x + d * 8, yy - 3.2);
      c.lineTo(x + d * 8, yy - 1.6);
      c.quadraticCurveTo(x, yy, x - d * 8, yy - 2 + tail);
      c.fill();
      c.strokeStyle = '#25502a';
      c.lineWidth = 0.5;
      c.stroke();
      for (let i = 0; i < 4; i++) {
        c.fillStyle = '#3c7a3a';
        c.fillRect(x - d * (4 - i * 2), yy - 4.6, 0.8, 0.8);
      }
      eye(x + d * 3.5, yy - 4.8);
      c.fillStyle = '#fff';
      for (let i = 0; i < 3; i++) c.fillRect(x + d * (5 + i * 1.1), yy - 1.9, 0.5, 0.5);
      return;
    }
    case 'fish': {
      const bob = Math.sin(t * 2.5) * 1.2;
      shadow(c, x, y, 4, 1.4, 0.18);
      c.fillStyle = 'rgba(180,225,255,0.35)';
      c.strokeStyle = 'rgba(255,255,255,0.7)';
      c.lineWidth = 0.5;
      c.beginPath();
      c.arc(x, yy - 8 + bob, 4.6, 0, Math.PI * 2);
      c.fill();
      c.stroke();
      const sw = Math.sin(t * 8) * 0.6;
      blob(x + d * 0.5, yy - 8 + bob, 2.4, 1.5, '#ff9f43');
      c.fillStyle = '#ff7a2f';
      c.beginPath();
      c.moveTo(x - d * 1.6, yy - 8 + bob);
      c.lineTo(x - d * 3.3, yy - 9.4 + bob + sw);
      c.lineTo(x - d * 3.3, yy - 6.6 + bob + sw);
      c.fill();
      eye(x + d * 1.8, yy - 8.4 + bob);
      c.fillStyle = 'rgba(255,255,255,0.7)';
      c.fillRect(x - 2.6, yy - 11 + bob, 1, 1);
      return;
    }
    case 'dino': {
      shadow(c, x, y, 5, 1.6);
      const step = pose.moving ? Math.sin(pose.phase * Math.PI * 2) : 0;
      blob(x - d * 4, yy - 4 + step * 0.3, 2.6, 1.2, '#6cc86a', -d * 0.4);
      blob(x, yy - 4.5, 3.6, 3, '#6cc86a');
      blob(x + d * 0.6, yy - 3.6, 2, 1.8, '#c9f0b0');
      blob(x + d * 2.8, yy - 9, 2.8, 2.4, '#6cc86a');
      for (const s of [-1, 1]) {
        c.fillStyle = '#4f9a4a';
        c.fillRect(x + s * 1.4 - 0.7, yy - 1.6 - Math.max(0, s * step), 1.4, 1.8);
      }
      for (let i = 0; i < 3; i++) {
        c.fillStyle = '#ffb347';
        c.beginPath();
        c.arc(x - d * (1 - i * 1.4), yy - 7.4 - (i === 1 ? 0.4 : 0), 0.7, 0, Math.PI * 2);
        c.fill();
      }
      eye(x + d * 3.6, yy - 9.6);
      return;
    }
  }
}

/** Horse / pony, seen in 3/4 view; `rider` draws the saddle so the player can sit on it. */
export function drawHorse(c: Ctx, x: number, y: number, pose: Pose, t: number, pony = false, saddle = false): void {
  const k = pony ? 0.8 : 1;
  const d = pose.facing === 'left' ? -1 : pose.facing === 'right' ? 1 : (Math.floor(x) % 2 ? 1 : -1);
  const coat = pony ? '#e8d2b0' : '#8a5530';
  const dark = pony ? '#b89a70' : '#5e381f';
  const mane = pony ? '#f4f1ea' : '#2a1810';
  const step = pose.moving ? Math.sin(pose.phase * Math.PI * 2) : 0;
  shadow(c, x, y, 13 * k, 3);
  // Legs (alternating gait).
  for (const [lx, ph] of [[-8, 0], [-4, 1], [5, 1], [9, 0]] as const) {
    const lift = (ph ? step : -step) * 1.6;
    c.fillStyle = dark;
    c.fillRect(x + lx * k * d - 1, y - 9 * k - Math.max(0, lift), 2.2, 9 * k);
    c.fillStyle = '#2a1810';
    c.fillRect(x + lx * k * d - 1.2, y - 1.2 - Math.max(0, lift), 2.6, 1.6);
  }
  // Body.
  c.fillStyle = coat;
  c.strokeStyle = shade(coat, -0.5);
  c.lineWidth = 0.6;
  c.beginPath();
  c.ellipse(x, y - 12 * k, 12 * k, 5.5 * k, 0, 0, Math.PI * 2);
  c.fill();
  c.stroke();
  c.fillStyle = 'rgba(255,255,255,0.18)';
  c.beginPath();
  c.ellipse(x - 2 * d, y - 14 * k, 8 * k, 2 * k, 0, 0, Math.PI * 2);
  c.fill();
  // Neck and head.
  const hx = x + d * 11 * k, hy = y - 20 * k + Math.sin(t * 2 + x) * 0.4;
  c.fillStyle = coat;
  c.beginPath();
  c.moveTo(x + d * 6 * k, y - 15 * k);
  c.lineTo(hx, hy - 3);
  c.lineTo(hx + d * 2, hy + 2);
  c.lineTo(x + d * 9 * k, y - 10 * k);
  c.closePath();
  c.fill();
  c.stroke();
  c.beginPath();
  c.ellipse(hx + d * 3 * k, hy + 1, 4.2 * k, 2.6 * k, d * 0.4, 0, Math.PI * 2);
  c.fill();
  c.stroke();
  c.fillStyle = '#231a24';
  c.fillRect(hx + d * 2 * k - 0.4, hy - 0.6, 0.9, 0.9);
  c.fillRect(hx + d * 6 * k - 0.4, hy + 1.6, 0.8, 0.6);
  c.fillStyle = coat;
  c.beginPath();
  c.moveTo(hx - d * 0.5, hy - 2.5);
  c.lineTo(hx + d * 0.5, hy - 5.5);
  c.lineTo(hx + d * 1.5, hy - 2.2);
  c.fill();
  // Mane and tail flowing with the wind.
  const flow = Math.sin(t * 3 + x) * 1.2;
  c.strokeStyle = mane;
  c.lineWidth = 1.4;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(hx - d * 1, hy - 3);
  c.quadraticCurveTo(x + d * 6 * k + flow, y - 18 * k, x + d * 4 * k, y - 15 * k);
  c.moveTo(x - d * 11 * k, y - 14 * k);
  c.quadraticCurveTo(x - d * 15 * k + flow, y - 10 * k, x - d * 13 * k + flow * 0.5, y - 5 * k);
  c.stroke();
  if (saddle) {
    c.fillStyle = '#7a2a2a';
    c.beginPath();
    c.roundRect(x - 5 * k, y - 18.5 * k, 10 * k, 4 * k, 1.5);
    c.fill();
    c.fillStyle = '#ffd34e';
    c.fillRect(x - 0.5, y - 15 * k, 1, 5 * k);
  }
}
